import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { AssignmentsWidget } from "./components/AssignmentsWidget";
import { StackedBarChartWidget } from "./components/StackedBarChartWidget";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { getDashboard } from "../../../services/api/apiClient";
import { DashboardAdminAssignmentsResult } from "../../../services/api/types/dashboard";
import { brand } from "../../../theme/themePrimitives";
import { usePageCustomisation } from "../../../hooks/usePageCustomisation";
import { useCustomisation } from "../../../context/CustomisationContext";
import { hasSomeCustomisation } from "../../../utils/helpers";
import { Spinner } from "../../../components/Spinner/Spinner";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatToBEDate = (d: Date) => {
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

export const buildLastNDaysBEDates = (days: number) =>
  Array.from({ length: days }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    return formatToBEDate(d);
  });

export const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { portalUid } = useParams();
  const [assignmentsData, setAssignmentsData] = useState<
    Array<{ id: string; value: number; label: string }>
  >([]);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [userSessionsSeries, setUserSessionsSeries] = useState<
    Array<{ id: string; label: string; data: number[]; color: string }>
  >([]);
  const [sessionDates, setSessionDates] = useState<string[]>([]);
  const [activitiesSeries, setActivitiesSeries] = useState<
    Array<{ id: string; label: string; data: number[]; color: string }>
  >([]);
  const [activityDates, setActivityDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  usePageCustomisation("HOME");
  const { customisations } = useCustomisation();
  const isHomeDesignLoaded = hasSomeCustomisation(
    customisations.customisations,
    "HOME",
  );

  usePageMetadata(
    {
      title: "Dashboard",
      breadcrumbs: [
        {
          label: "Analytics",
          path: `/portal-configuration/${portalUid}/dashboard`,
        },
        {
          label: "Dashboard",
        },
      ],
    },
    [portalUid],
  );

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!portalUid) return;

      try {
        setLoading(true);
        const dashboardResponse = await getDashboard(portalUid);

        // Type guard to ensure we have admin dashboard result
        if (!("dashboardAssignmentsResult" in dashboardResponse)) {
          console.error(
            "Expected admin dashboard result but received user dashboard result",
          );
          return;
        }

        const adminDashboard =
          dashboardResponse as DashboardAdminAssignmentsResult;
        const assignmentsResponse = adminDashboard.dashboardAssignmentsResult;
        const sessionsResponse = adminDashboard.dashboardSessionResult;
        const activitiesResponse = adminDashboard.dashboardActivitiesResult;

        // Transform assignments data
        const assignments = [
          {
            id: "complete",
            value: assignmentsResponse.completedAssignments,
            label: "Complete",
          },
          {
            id: "pending",
            value: assignmentsResponse.pendingAssignments,
            label: "Pending",
          },
          {
            id: "overdue",
            value: assignmentsResponse.overdueAssignments,
            label: "Overdue",
          },
        ];
        setAssignmentsData(assignments);
        setTotalAssignments(assignmentsResponse.activeAssignments);

        // Transform sessions data
        const sessionsArray = Array.isArray(sessionsResponse)
          ? sessionsResponse
          : [];
        const axisDates = buildLastNDaysBEDates(5); // last 5 days including today
        const dates = sessionsArray.map((session) => session.date);
        const sessionCounts = sessionsArray.map(
          (session) => session.sessionCount,
        );

        if (sessionsArray.length === 0) {
          setSessionDates(axisDates);
          setUserSessionsSeries([
            {
              id: "sessions-1",
              label: "Active Sessions",
              data: axisDates.map(() => 0),
              color: brand[600],
            },
          ]);
        } else {
          setSessionDates(dates);
          setUserSessionsSeries([
            {
              id: "sessions-1",
              label: "Active Sessions",
              data: sessionCounts,
              color: brand[600],
            },
          ]);
        }

        // Transform activities data
        const activitiesArray = Array.isArray(activitiesResponse)
          ? activitiesResponse
          : [];
        const activityDatesData =
          activitiesArray.map((activity) => activity.date) || [];
        const completedCounts = activitiesArray.map(
          (activity) => activity.activitiesCountCompleted,
        );
        const enrollmentCounts = activitiesArray.map(
          (activity) => activity.activitiesCountEnrollments,
        );

        setActivityDates(activityDatesData);
        setActivitiesSeries([
          {
            id: "completed",
            label: "Completed",
            data: completedCounts,
            color: brand[600],
          },
          {
            id: "enrolments",
            label: "Enrolments",
            data: enrollmentCounts,
            color: theme.palette.warning.main,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [portalUid, theme.palette.primary.main, theme.palette.warning.main]);

  if (loading || !isHomeDesignLoaded) {
    return <Spinner />;
  }

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 2fr",
            lg: "1fr 2fr",
          },
          gap: 3,
          mb: 3,
        }}
      >
        <AssignmentsWidget data={assignmentsData} total={totalAssignments} />
        <StackedBarChartWidget
          series={userSessionsSeries}
          dates={sessionDates}
          hideLegend
          title="User sessions"
        />
      </Box>

      <Box>
        <StackedBarChartWidget
          height={250}
          hideLegend={false}
          title="Activities"
          series={activitiesSeries}
          dates={activityDates}
        />
      </Box>
    </>
  );
};
